import { useState, useCallback, useMemo } from "react";
import type { JSX } from "react";
import { ASK_USER_MARKER_CLOSER, ASK_USER_MARKER_PREFIX } from "@magnis/client-core";
import { Icon } from "../ui/Icon";
import type { AskUserPayload, AskUserQuestion as AskUserQuestionType } from "../../modules/episodes/types";

export interface AskUserCardProps {
  readonly payload: AskUserPayload;
  readonly submitted: boolean;
  readonly onSubmit: (formattedAnswer: string) => void;
}

type Answers = Record<string, string | string[]>;

export function AskUserCard({
  payload,
  submitted,
  onSubmit,
}: AskUserCardProps): JSX.Element {
  const tabs = payload.tabs;
  const [answers, setAnswers] = useState<Answers>({});
  const [otherTexts, setOtherTexts] = useState<Record<string, string>>({});
  const [step, setStep] = useState(0);

  // Flatten all questions across tabs into a single ordered list
  const allQuestions = useMemo(() => {
    const result: { tabLabel: string; question: AskUserQuestionType }[] = [];
    for (const tab of tabs) {
      for (const q of tab.questions) {
        result.push({ tabLabel: tab.label, question: q });
      }
    }
    return result;
  }, [tabs]);

  const totalSteps = allQuestions.length;
  const current = allQuestions[step];

  const handleSelectOne = useCallback(
    (questionId: string, optionId: string) => {
      if (submitted) return;
      setAnswers((prev) => ({ ...prev, [questionId]: optionId }));
      if (optionId !== "__other__") {
        setOtherTexts((prev) => {
          const next = { ...prev };
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete next[questionId];
          return next;
        });
      }
    },
    [submitted],
  );

  const handleSelectMany = useCallback(
    (questionId: string, optionId: string) => {
      if (submitted) return;
      setAnswers((prev) => {
        const current = (prev[questionId] as string[] | undefined) ?? [];
        const next = current.includes(optionId)
          ? current.filter((id) => id !== optionId)
          : [...current, optionId];
        return { ...prev, [questionId]: next };
      });
      if (optionId !== "__other__") return;
      setOtherTexts((prev) => {
        const current = (answers[questionId] as string[] | undefined) ?? [];
        if (current.includes("__other__")) {
          const next = { ...prev };
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete next[questionId];
          return next;
        }
        return prev;
      });
    },
    [submitted, answers],
  );

  const handleOtherText = useCallback(
    (questionId: string, text: string) => {
      if (submitted) return;
      setOtherTexts((prev) => ({ ...prev, [questionId]: text }));
    },
    [submitted],
  );

  const handleSubmit = useCallback(() => {
    const lines: string[] = [ASK_USER_MARKER_PREFIX];
    for (const tab of tabs) {
      for (const q of tab.questions) {
        const answer = answers[q.id];
        if (!answer) continue;

        const resolveLabel = (id: string): string => {
          if (id === "__other__") return otherTexts[q.id] ?? "";
          return q.options.find((o) => o.id === id)?.label ?? id;
        };

        if (q.type === "select_one") {
          lines.push(`${q.text}: ${resolveLabel(answer as string)}`);
        } else {
          const selected = answer as string[];
          const labels = selected.map(resolveLabel).filter(Boolean);
          lines.push(`${q.text}: ${labels.join(", ")}`);
        }
      }
    }
    lines.push(ASK_USER_MARKER_CLOSER);
    onSubmit(lines.join("\n"));
  }, [tabs, answers, otherTexts, onSubmit]);

  const isCurrentAnswered = current
    ? isQuestionAnswered(current.question, answers, otherTexts)
    : false;

  const allAnswered = allQuestions.every(({ question: q }) =>
    isQuestionAnswered(q, answers, otherTexts),
  );

  const isLastStep = step === totalSteps - 1;

  // Find next unanswered step (for Next button)
  const findNextUnanswered = (): number => {
    for (let i = step + 1; i < totalSteps; i++) {
      const q = allQuestions[i];
      if (q && !isQuestionAnswered(q.question, answers, otherTexts)) return i;
    }
    for (let i = 0; i < step; i++) {
      const q = allQuestions[i];
      if (q && !isQuestionAnswered(q.question, answers, otherTexts)) return i;
    }
    return step + 1 < totalSteps ? step + 1 : step;
  };

  const handleNext = (): void => {
    if (isLastStep) return;
    setStep(findNextUnanswered());
  };

  const [expanded, setExpanded] = useState(false);

  if (submitted) {
    return (
      <div className="rounded-xl border border-edge bg-surface-tertiary px-3.5 py-3">
        <button
          type="button"
          className="flex w-full items-center justify-between"
          onClick={() => { setExpanded((v) => !v); }}
        >
          <div className="flex items-center gap-2">
            <span className="text-emerald-400">
              <Icon name="circle-check" size={14} />
            </span>
            <span className="text-[13px] font-semibold text-agent-text-heading">
              Answered
            </span>
          </div>
          <span className="text-agent-text-dim">
            <Icon name={expanded ? "chevron-up" : "chevron-down"} size={16} />
          </span>
        </button>
        {expanded && (
          <div className="mt-2 space-y-1">
            {allQuestions.map(({ question: q }) => {
              const answer = answers[q.id];
              if (!answer) return null;
              const resolveLabel = (id: string): string => {
                if (id === "__other__") return otherTexts[q.id] ?? "";
                return q.options.find((o) => o.id === id)?.label ?? id;
              };
              const label =
                q.type === "select_one"
                  ? resolveLabel(answer as string)
                  : (answer as string[]).map(resolveLabel).filter(Boolean).join(", ");
              return (
                <p key={q.id} className="text-[12px] text-agent-text-muted">
                  <span className="text-agent-text-dim">{q.text}:</span> {label}
                </p>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  if (!current) return <></>;

  return (
    <div className="rounded-xl border border-edge bg-surface-tertiary px-3.5 py-3">
      {/* Header: icon + question text + pagination */}
      <div className="mb-2.5 flex items-center gap-2">
        <span className="text-blue-400 shrink-0">
          <Icon name="message-circle" size={14} />
        </span>
        <span className="text-[13px] font-semibold text-agent-text-heading flex-1 min-w-0">
          {current.question.text}
        </span>
        {totalSteps > 1 && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              disabled={step === 0}
              className="text-agent-text-muted hover:text-agent-text disabled:opacity-25 disabled:cursor-default cursor-pointer bg-transparent border-none"
              onClick={() => { setStep((s) => s - 1); }}
            >
              <Icon name="chevron-left" size={16} />
            </button>
            <span className="text-[11px] text-agent-text-dim min-w-[24px] text-center">
              {step + 1}/{totalSteps}
            </span>
            <button
              type="button"
              disabled={step === totalSteps - 1}
              className="text-agent-text-muted hover:text-agent-text disabled:opacity-25 disabled:cursor-default cursor-pointer bg-transparent border-none"
              onClick={() => { setStep((s) => s + 1); }}
            >
              <Icon name="chevron-right" size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Options — one per line */}
      <QuestionView
        question={current.question}
        answer={answers[current.question.id]}
        otherText={otherTexts[current.question.id] ?? ""}
        onSelectOne={handleSelectOne}
        onSelectMany={handleSelectMany}
        onOtherText={handleOtherText}
      />

      {/* Footer: Next or Submit */}
      <div className="mt-3 flex items-center">
        {isLastStep || totalSteps === 1 ? (
          <button
            type="button"
            disabled={!allAnswered}
            className={`flex items-center gap-1.5 rounded-md px-4 py-1.5 text-[12px] font-medium transition-colors ${
              allAnswered
                ? "bg-blue-500 text-white hover:bg-blue-400 cursor-pointer"
                : "bg-white/5 text-agent-text-dim cursor-default"
            }`}
            onClick={handleSubmit}
          >
            <Icon name="send" size={13} />
            Submit
          </button>
        ) : (
          <button
            type="button"
            disabled={!isCurrentAnswered}
            className={`flex items-center gap-1.5 rounded-md px-4 py-1.5 text-[12px] font-medium transition-colors ${
              isCurrentAnswered
                ? "bg-blue-500 text-white hover:bg-blue-400 cursor-pointer"
                : "bg-white/5 text-agent-text-dim cursor-default"
            }`}
            onClick={handleNext}
          >
            Next
            <Icon name="chevron-right" size={13} />
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────

function isQuestionAnswered(
  q: AskUserQuestionType,
  answers: Answers,
  otherTexts: Record<string, string>,
): boolean {
  const a = answers[q.id];
  if (!a) return false;
  if (q.type === "select_many" && Array.isArray(a) && a.length === 0) return false;
  if (
    (a === "__other__" || (Array.isArray(a) && a.includes("__other__"))) &&
    !otherTexts[q.id]?.trim()
  )
    return false;
  return true;
}

// ─── Single question renderer ────────────────────────────────────────

interface QuestionViewProps {
  readonly question: AskUserQuestionType;
  readonly answer: string | string[] | undefined;
  readonly otherText: string;
  readonly onSelectOne: (qId: string, optionId: string) => void;
  readonly onSelectMany: (qId: string, optionId: string) => void;
  readonly onOtherText: (qId: string, text: string) => void;
}

function QuestionView({
  question: q,
  answer,
  otherText,
  onSelectOne,
  onSelectMany,
  onOtherText,
}: QuestionViewProps): JSX.Element {
  const isSelectOne = q.type === "select_one";
  const showOtherInput =
    answer === "__other__" ||
    (Array.isArray(answer) && answer.includes("__other__"));

  return (
    <div className="space-y-1">
      {q.options.map((opt) => {
        const isSelected = isSelectOne
          ? answer === opt.id
          : Array.isArray(answer) && answer.includes(opt.id);

        return (
          <button
            key={opt.id}
            type="button"
            className={`block w-full rounded-lg border px-3 py-1.5 text-left text-[12px] transition-colors cursor-pointer ${
              isSelected
                ? "border-blue-400 bg-blue-500/15 text-blue-300"
                : "border-agent-border text-agent-text-muted hover:bg-white/5"
            }`}
            onClick={() =>
              // eslint-disable-next-line @typescript-eslint/no-unused-expressions
              { isSelectOne
                ? onSelectOne(q.id, opt.id)
                : onSelectMany(q.id, opt.id); }
            }
          >
            {opt.label}
          </button>
        );
      })}
      {/* Other option */}
      <button
        type="button"
        className={`block w-full rounded-lg border px-3 py-1.5 text-left text-[12px] italic transition-colors cursor-pointer ${
          showOtherInput
            ? "border-blue-400 bg-blue-500/15 text-blue-300"
            : "border-agent-border text-agent-text-muted hover:bg-white/5"
        }`}
        onClick={() =>
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          { isSelectOne
            ? onSelectOne(q.id, "__other__")
            : onSelectMany(q.id, "__other__"); }
        }
      >
        Other…
      </button>
      {showOtherInput && (
        <input
          type="text"
          className="w-full rounded-lg border border-agent-border bg-surface px-3 py-1.5 text-[12px] text-agent-text placeholder:text-agent-text-dim focus:border-blue-400 focus:outline-none"
          placeholder="Type your answer…"
          value={otherText}
          onChange={(e) => { onOtherText(q.id, e.target.value); }}
        />
      )}
    </div>
  );
}
